import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  GxDropdownComponent,
  ignorePromise,
  MenuItem,
  slideInFromRight,
  addParamForRoute
} from '@ebcont/ebcont.galaxy.js';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbstractGroupedList } from '@hoc/hoc-grouped-list';
import { environment } from '@env/environment';
import {
  skillDownloadMapper,
  SkillFilterBuilder,
  SkillListItem,
  skillListItemGroupingOperator,
  updateSkillFilter
} from '@core/models/skill';
import { InjectorKeys } from '@core/utils/injector.keys';
import { BackendService } from '@core/prototypes/backend.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationConfiguration } from '@core/services/configuration/application.configuration.interface';
import { UserService } from '@core/services/user/user.service';
import { Observable } from 'rxjs';
import { SkillsService } from '@core/services/employee/skills.service';
import { map } from 'rxjs/operators';
import { SIMPLE_EVENTS, textAsMenuItem } from '@core/utils/utils';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { ListItem } from '@core/models/list-item.model';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-skill-list',
  templateUrl: './skill-list.component.html',
  styleUrls: ['./skill-list.component.css'],
  animations: [slideInFromRight],
  standalone: false
})
export class SkillListComponent extends AbstractGroupedList<SkillListItem> implements OnInit {
  @Input() isExtended = true;
  @ViewChild('footerContainer') footerBlock: TemplateRef<Element> | undefined;

  searchValue = '';
  autocompleteData: MenuItem[] = [];
  skillId?: string;
  totalNumberResultsets = 0;

  public pageTitle: string = '';

  public selectedSkill: SkillListItem | undefined;

  protected initialized = false;

  /** The choices to search for. */
  public searchForOptions: MenuItem[] = [
    {
      label: 'skill.plural',
      isRouterLink: false,
      id: 'skill'
    },
    {
      label: 'coreCompetence.plural',
      isRouterLink: false,
      id: 'coreCompetence'
    }
  ];
  /** The actual choice to search for. */
  public searchFor = this.searchForOptions[0];

  constructor(
    @Inject(InjectorKeys.SKILL) private service: BackendService<SkillListItem>,
    cd: ChangeDetectorRef,
    router: Router,
    route: ActivatedRoute,
    @Inject(InjectorKeys.CONFIGURATION) public config: ApplicationConfiguration,
    private userService: UserService,
    private translate: TranslateService
  ) {
    super(service, skillListItemGroupingOperator, config, cd, router, route);
  }

  ngOnInit(): void {
    this.filter = new SkillFilterBuilder(this.config).build();
    Reflect.set(this.filter, 'type', this.searchFor.id);

    this.route.queryParams.subscribe((queryParams) => {
      const type = this.searchForOptions.find((option) => {
        return option.id === queryParams['type'];
      });
      if (type) {
        this.searchFor = type;
        Reflect.set(this.filter, 'type', this.searchFor.id);
      }

      Reflect.set(this.filter, 'stars', queryParams['stars']);
      Reflect.set(this.filter, 'tab', queryParams['tab']);
      this.skillId = queryParams['skill'];
      Reflect.set(this.filter, 'skill', this.skillId);

      const pageNumber = parseInt(queryParams['pageNumber'] || 0);
      const pageSize = parseInt(queryParams['pageSize'] || this.config.pageSize);
      this.filter.pageNumber = pageNumber;
      this.filter.pageSize = pageSize;
      this.pagination.currentPage = pageNumber;
      this.pagination.pageSize = pageSize;

      this.onReloaded();
      this.cd.detectChanges();
    });

    this.initialized = true;
    this.cd.markForCheck();
  }

  public handleEmptyList(): void {
    this.searchValue = '';
  }

  /**
   * Reloads the current page with the current filter values in the query parameter.
   */
  private applyFilter() {
    this.sorting = Reflect.get(this.filter, 'sorting');
    ignorePromise(this.router.navigate(['skills'], { queryParams: this.filter }));
    this.reload();
  }

  filterSelected(item: MenuItem[]) {
    this.searchFor = item[0];
    Reflect.set(this.filter, 'type', this.searchFor.id);
    Reflect.set(this.filter, 'sorting', this.sorting);
    this.filter.pageNumber = 0;
    this.applyFilter();
  }

  /**
   * Searches for a name to serve the autocomplete dropdown.
   * @param text
   */
  protected searchForName = (text: string): Observable<MenuItem[]> => {
    const service = this.service as SkillsService;
    return service.autocompleteSkills(text, this.searchFor.id).pipe(
      map((suggest: string[]) => {
        return suggest.map((item) => {
          return textAsMenuItem(item);
        });
      })
    );
  };

  override onReloaded() {
    super.onReloaded();
    const allSkills = Array.from(this.items.values()).flat();
    if (allSkills.length === 0) {
      return;
    }

    this.selectedSkill = allSkills.find((item) => {
      return item.skill === this.skillId;
    });
    if (!this.selectedSkill) {
      this.skillId = undefined;
    }

    if (this.searchFor.id === 'skill') {
      this.pageTitle = this.translate.instant('skill.skillSize', { skills: this.lastResult.totalNumberResultsets });
    } else {
      this.pageTitle = this.translate.instant('skill.coreCompetenceSize', {
        skills: this.lastResult.totalNumberResultsets
      });
    }
  }

  protected createCaptionForDropdown(dropdown: GxDropdownComponent): string {
    const dropdownLabel = dropdown?.label ?? '';
    const selectedItemLabel = dropdown.selectedItems[0]?.label ?? '';
    const dropdownLabelText = dropdownLabel ? dropdown.translate.instant(dropdownLabel) : '';
    const dropdownItemText = selectedItemLabel ? dropdown.translate.instant(selectedItemLabel) : '';
    return `${dropdownLabelText} ${dropdownItemText}`.trim();
  }

  filterBlockChanged(value: Record<string, string>) {
    const oldFilter = this.filterAsString(); // the current filter
    this.filter = updateSkillFilter(this.filter, value);
    const newFilter = this.filterAsString(); // the new filter
    if (oldFilter !== newFilter && this.initialized) {
      this.applyFilter();
    }
  }

  private filterAsString() {
    return JSON.stringify({ ...this.filter, skill: null, tab: null, stars: null });
  }

  clickedOnSkill(item: SkillListItem) {
    ignorePromise(
      addParamForRoute(this.router, this.route, { skill: item.skill }).then(() => {
        this.skillId = item.skill;
        this.onReloaded();
      })
    );
  }

  closeDrawer() {
    ignorePromise(addParamForRoute(this.router, this.route, { tab: null, skill: null, stars: null }));
    this.skillId = undefined;
    this.onReloaded();
  }

  protected readonly csvExportIcon = faDownload;

  public downloadTheList() {
    ignorePromise(
      this.backendService.downloadList(
        {
          ...this.filter,
          forDownload: true
        },
        skillDownloadMapper,
        this.searchFor.id === 'skill' ? 'skills' : 'coreCompetences',
        this.pagination.totalItems
      )
    );
  }

  public getSkillListItem(skillItem: SkillListItem) {
    let skillListItemCount = '';

    if (skillItem.count) {
      if (skillItem.count <= 1) {
        skillListItemCount = this.translate.instant('team.size.singular', { persons: skillItem.count || 0 });
      } else {
        skillListItemCount = this.translate.instant('team.size.plural', { persons: skillItem.count });
      }
    }

    let data: ListItem = {
      columns: [
        {
          lines: [{ content: skillItem.skill || 'n/a' }]
        },
        {
          lines: [{ content: skillListItemCount }]
        }
      ]
    };
    return data;
  }
}
